import { useState, useEffect } from 'react'
import {
  getUserProfile,
  saveUserProfile,
  linkGoogleAccount,
  unlinkGoogleAccount,
  getUserIdByGoogleId,
  deleteUserState,
} from '../../lib/userProfile'
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import { BaseModal } from './BaseModal'
import { getStateFromAPI } from '../../lib/api-v2'
import { setItem, idKey } from '../../lib/localStorage'
import { adoptUserId } from '../../lib/sessionUser'

type Props = {
  isOpen: boolean
  handleClose: () => void
  userId: string
  currentDisplayName?: string
  onProfileUpdate?: (displayName: string, isLinked: boolean) => void
}

export const ProfileModal = ({
  isOpen,
  handleClose,
  userId,
  currentDisplayName,
  onProfileUpdate,
}: Props) => {
  const [displayNameInput, setDisplayNameInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isGoogleLinked, setIsGoogleLinked] = useState(false)
  const [googleEmail, setGoogleEmail] = useState('')
  const [googleName, setGoogleName] = useState('')
  const [showSwitchWarning, setShowSwitchWarning] = useState(false)
  const [pendingGoogleUser, setPendingGoogleUser] = useState<any>(null)
  const [existingUserId, setExistingUserId] = useState<string | null>(null)
  const [deleteOldProfile, setDeleteOldProfile] = useState(true) // Default: törli a régi profilt

  const auth = getAuth()

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true)
      setError('')
      try {
        const profile = await getUserProfile(userId)
        if (profile) {
          setDisplayNameInput(profile.displayName || '')
          setIsGoogleLinked(!!profile.googleId)
          setGoogleEmail(profile.googleEmail || '')
          setGoogleName(profile.googleName || '')
        }
      } catch (err) {
        setError('Hiba a profil betöltése során')
      } finally {
        setLoading(false)
      }
    }

    if (isOpen) {
      loadProfile()
    }
  }, [isOpen, userId])

  const handleSaveDisplayName = async () => {
    if (!displayNameInput.trim()) {
      setError('A név nem lehet üres')
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const result = await saveUserProfile(userId, displayNameInput.trim())
      if (result.success) {
        setSuccess('Név sikeresen elmentve!')
        onProfileUpdate?.(displayNameInput.trim(), isGoogleLinked)
        setTimeout(() => {
          setSuccess('')
        }, 3000)
      } else {
        setError(result.error || 'Hiba történt a mentés során')
      }
    } catch (err) {
      setError('Hiba történt a mentés során')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLink = async () => {
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      const user = result.user

      if (user) {
        // Check if this Google account is already linked to another profile
        const linkedUserId = await getUserIdByGoogleId(user.uid)

        if (linkedUserId && linkedUserId !== userId) {
          adoptUserId(linkedUserId)
          setLoading(false)
          return
        }

        // Scenario 1: No link yet, or Scenario 2: Already linked to current profile
        const linkResult = await linkGoogleAccount(userId, user)
        if (linkResult.success) {
          setIsGoogleLinked(true)
          setGoogleEmail(user.email || '')
          setGoogleName(user.displayName || '')

          // Ha nincs még display name, használjuk a Google nevet
          if (!displayNameInput.trim() && user.displayName) {
            setDisplayNameInput(user.displayName)
          }

          setSuccess('Google fiók sikeresen összekapcsolva!')
          onProfileUpdate?.(displayNameInput || user.displayName || '', true)
          setTimeout(() => {
            setSuccess('')
          }, 3000)
        } else {
          setError(linkResult.error || 'Hiba az összekapcsolás során')
        }
      }
    } catch (err: any) {
      console.error('Google link error:', err)
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Bejelentkezés megszakítva')
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('Ez az email cím már használatban van másik fiókkal')
      } else {
        setError('Hiba történt a Google bejelentkezés során')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSwitchProfile = async () => {
    if (!pendingGoogleUser || !existingUserId) return

    setLoading(true)
    setError('')

    try {
      // IMPORTANT: Save the old userId BEFORE clearing localStorage
      // The userId prop might already be the new Google userId
      const oldUserId = localStorage.getItem('szozat-gameState') || userId

      // 1. Delete old profile's game state from Firebase if requested
      if (deleteOldProfile && oldUserId) {
        console.log('Deleting old profile data from Firebase:', oldUserId)
        await deleteUserState(oldUserId)
      }

      // 2. Clear all localStorage data to remove old profile's game state
      // Keep only UI preferences
      const keysToKeep = ['szozat-theme', 'szozat-view']
      const currentStorage: Record<string, string> = {}

      // Save items we want to keep
      keysToKeep.forEach((key) => {
        const value = localStorage.getItem(key)
        if (value !== null) {
          currentStorage[key] = value
        }
      })

      // Clear everything
      localStorage.clear()

      // Restore items we kept
      Object.entries(currentStorage).forEach(([key, value]) => {
        localStorage.setItem(key, value)
      })

      // 3. Set the new userId in localStorage
      localStorage.setItem('szozat-gameState', existingUserId)

      // 4. Load new profile data from Firebase
      console.log('Loading new profile data from Firebase:', existingUserId)
      const newState = await getStateFromAPI()
      console.log('Loaded new profile state:', newState)

      // 5. Save new profile data to localStorage
      if (newState) {
        // Store the state id
        setItem(idKey, JSON.stringify({ id: existingUserId }))

        // Store other state data that should persist
        // The reducer will handle the full state on reload
        console.log('New profile data loaded successfully')
      } else {
        console.log('No state found for new profile - will create fresh state')
      }

      // Clear the warning state
      setShowSwitchWarning(false)
      setPendingGoogleUser(null)
      setExistingUserId(null)

      // Close modal
      handleClose()

      // 6. Reload to apply new profile
      console.log('Reloading to apply new profile...')
      window.location.reload()
    } catch (err) {
      console.error('Profile switch error:', err)
      setError('Hiba történt a profilváltás során')
      setLoading(false)
    }
  }

  const handleCancelSwitch = async () => {
    // User cancelled the switch, sign out from Google
    setShowSwitchWarning(false)
    setPendingGoogleUser(null)
    setExistingUserId(null)

    try {
      await signOut(auth)
    } catch (err) {
      console.error('Error signing out:', err)
    }
  }

  const handleGoogleUnlink = async () => {
    if (
      // eslint-disable-next-line no-restricted-globals
      !confirm(
        'Biztosan leválasztod a Google fiókodat? A játékállásod megmarad.'
      )
    ) {
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const result = await unlinkGoogleAccount(userId)
      if (result.success) {
        setIsGoogleLinked(false)
        setGoogleEmail('')
        setGoogleName('')
        setSuccess('Google fiók sikeresen leválasztva!')
        onProfileUpdate?.(displayNameInput, false)

        // Sign out from Firebase auth
        await signOut(auth)

        setTimeout(() => {
          setSuccess('')
        }, 3000)
      } else {
        setError(result.error || 'Hiba a leválasztás során')
      }
    } catch (err) {
      setError('Hiba történt a leválasztás során')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <BaseModal
        title="Profil beállítások"
        isOpen={isOpen}
        handleClose={handleClose}
        zIndex={1020}
      >
        <div className="space-y-6 px-2">
          {/* Display Name Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Megjelenített név
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={displayNameInput}
                onChange={(e) => setDisplayNameInput(e.target.value)}
                onKeyPress={(e) => {
                  if (
                    e.key === 'Enter' &&
                    !loading &&
                    displayNameInput.trim()
                  ) {
                    handleSaveDisplayName()
                  }
                }}
                className="display-name flex-1 px-3 py-2 border rounded-md text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Add meg a neved"
                maxLength={20}
                disabled={loading}
              />
              <button
                onClick={handleSaveDisplayName}
                disabled={
                  loading ||
                  !displayNameInput.trim() ||
                  displayNameInput === currentDisplayName
                }
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? '...' : 'Mentés'}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Ez a név fog megjelenni a ranglistákon és szobákban
            </p>
          </div>

          {/* Google Account Section */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Google fiók
            </label>

            {!isGoogleLinked ? (
              <div>
                <button
                  onClick={handleGoogleLink}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {loading ? 'Csatlakozás...' : 'Összekapcsolás Google fiókkal'}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  A Google fiókkal való összekapcsolás megkönnyíti a
                  bejelentkezést, és szinkronizálja a játékállásodat eszközök
                  között.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">
                      Összekapcsolva
                    </p>
                    {googleEmail && (
                      <p className="text-xs text-green-600 dark:text-green-400 truncate">
                        {googleEmail}
                      </p>
                    )}
                    {googleName && (
                      <p className="text-xs text-green-600 dark:text-green-400">
                        {googleName}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleGoogleUnlink}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-md text-sm hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Leválasztás...' : 'Google fiók leválasztása'}
                </button>
              </div>
            )}
          </div>

          {/* Messages */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
              <p className="text-sm text-green-600 dark:text-green-400">
                {success}
              </p>
            </div>
          )}
        </div>
      </BaseModal>

      {/* Warning Modal for Profile Switch */}
      {showSwitchWarning && (
        <BaseModal
          title="⚠️ Figyelmeztetés"
          isOpen={showSwitchWarning}
          handleClose={handleCancelSwitch}
          zIndex={1030}
        >
          <div className="space-y-4 px-2">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Ez a Google fiók már használatban van egy másik profilnál.
            </p>
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-2">
                Mit jelent ez?
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Ha folytatod, a jelenlegi (lokális) játékállapotod elvész, és
                átváltunk a Google fiókhoz kapcsolt profilra.
              </p>
            </div>
            {pendingGoogleUser && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Google fiók:
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {pendingGoogleUser.email}
                </p>
              </div>
            )}

            {/* Checkbox for deleting old profile data */}
            <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
              <input
                type="checkbox"
                id="deleteOldProfile"
                checked={deleteOldProfile}
                onChange={(e) => setDeleteOldProfile(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
              />
              <label
                htmlFor="deleteOldProfile"
                className="flex-1 text-xs text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                Töröld a régi profil játékállapotát
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleCancelSwitch}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mégsem
              </button>
              <button
                onClick={handleSwitchProfile}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md text-sm hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Váltás...' : 'Profil váltás'}
              </button>
            </div>
          </div>
        </BaseModal>
      )}
    </>
  )
}
